import React from 'react';
import { EffectComposer, Bloom, N8AO } from '@react-three/postprocessing';
import { resolveMultisampling } from './postProcessingConfig.js';

export {
  DEFAULT_MULTISAMPLING,
  resolveMultisampling,
} from './postProcessingConfig.js';

export interface PostProcessingEffectsProps {
  ssao?: boolean;
  ssaoIntensity?: number;
  ssaoRadius?: number;
  bloom?: boolean;
  bloomIntensity?: number;
  bloomThreshold?: number;
  bloomRadius?: number;
  /**
   * MSAA sample count for the EffectComposer's render target. Higher is
   * smoother but costs fill-rate + memory. Default 2 — a good quality/cost
   * balance for the thin metallic edges in XTS modules. Set 0 to disable.
   */
  multisampling?: number;
}

const SsaoEffect: React.FC<{ intensity: number; radius: number }> = ({
  intensity,
  radius,
}) => <N8AO aoRadius={radius} intensity={intensity} halfRes distanceFalloff={0.5} />;

const BloomEffect: React.FC<{
  intensity: number;
  threshold: number;
  radius: number;
}> = ({ intensity, threshold, radius }) => (
  <Bloom
    intensity={intensity}
    luminanceThreshold={threshold}
    luminanceSmoothing={radius}
    mipmapBlur
  />
);

export const PostProcessingEffects: React.FC<PostProcessingEffectsProps> = ({
  ssao = false,
  ssaoIntensity = 1.0,
  ssaoRadius = 20,
  bloom = false,
  bloomIntensity = 0.4,
  bloomThreshold = 0.85,
  bloomRadius = 0.4,
  multisampling,
}) => {
  const effects: React.ReactElement[] = [];
  if (ssao) {
    effects.push(
      <SsaoEffect key="ssao" intensity={ssaoIntensity} radius={ssaoRadius} />,
    );
  }
  if (bloom) {
    effects.push(
      <BloomEffect
        key="bloom"
        intensity={bloomIntensity}
        threshold={bloomThreshold}
        radius={bloomRadius}
      />,
    );
  }
  if (effects.length === 0) return null;
  return (
    <EffectComposer multisampling={resolveMultisampling(multisampling)}>
      {effects}
    </EffectComposer>
  );
};
